import { useState, useEffect } from 'react'
import styles from './AddUserToGroupModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

/**
 * AddUserToGroupModal - Modal for selecting available workspace members to add to a specific group
 * 
 * This modal displays a dropdown of workspace members that are not already in the group,
 * allowing the user to select and add them to the group.
 */
export default function AddUserToGroupModal({ 
    isOpen, 
    onClose, 
    onSave,
    group,
    apiToken = null
}) {
    const [selectedMemberId, setSelectedMemberId] = useState('')
    const [availableMembers, setAvailableMembers] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingMembers, setLoadingMembers] = useState(false)
    const [error, setError] = useState('')

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !loading && !loadingMembers)

    // Load available workspace members when modal opens
    useEffect(() => {
        if (isOpen && apiToken && group) {
            loadAvailableMembers()
        }
    }, [isOpen, apiToken, group])

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedMemberId('')
            setError('')
        }
    }, [isOpen])

    const loadAvailableMembers = async () => {
        try {
            setLoadingMembers(true)
            apiClient.setAuthToken(apiToken)
            
            // Get all workspace teammates/members
            const membersResponse = await apiClient.getTeammates()
            const allMembers = membersResponse.teammates || []
            
            // Filter out members who are already in this group
            const existingMemberIds = (group.members || []).map(member => member.id)
            const available = allMembers.filter(member => 
                !existingMemberIds.includes(member.id)
            )
            
            setAvailableMembers(available)
        } catch (err) {
            console.error('Failed to load workspace members:', err)
            setError('Failed to load workspace members. Please try again.')
            setAvailableMembers([])
        } finally {
            setLoadingMembers(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!selectedMemberId) {
            setError('Please select a member')
            return
        }

        if (!group) {
            setError('Group information is missing')
            return
        }

        setLoading(true)
        setError('')

        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }

            apiClient.setAuthToken(apiToken)
            
            const response = await apiClient.addMemberToGroup(group.id, selectedMemberId)
            
            // Notify parent component of successful addition
            if (onSave) {
                const selectedMember = availableMembers.find(m => m.id === selectedMemberId)
                onSave(selectedMember)
            }
            
            onClose()
        } catch (err) {
            console.error('Failed to add member to group:', err)
            setError(err.message || 'Failed to add member to group. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (!loading) {
            onClose()
        }
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Add User to Group</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* Group Information Display */}
                    {group && (
                        <div className={styles.groupInfo}>
                            <div className={styles.groupIcon}>
                                👥
                            </div>
                            <div className={styles.groupDetails}>
                                <h3>{group.name}</h3>
                                <p>{group.description || 'No description'}</p>
                                <span className={styles.memberCount}>{group.member_count || 0} members</span>
                            </div>
                        </div>
                    )}

                    {/* Member Selection */}
                    <div className={styles.formGroup}>
                        <label htmlFor="memberSelect" className={styles.label}>
                            Select Member to Add *
                        </label>
                        {loadingMembers ? (
                            <div className={styles.loadingMessage}>
                                <p>Loading workspace members...</p>
                            </div>
                        ) : availableMembers.length > 0 ? (
                            <select
                                id="memberSelect"
                                className={styles.select}
                                value={selectedMemberId}
                                onChange={(e) => setSelectedMemberId(e.target.value)}
                                disabled={loading}
                                required
                            >
                                <option value="">Choose a member...</option>
                                {availableMembers.map(member => (
                                    <option key={member.id} value={member.id}>
                                        {member.full_name || member.display_name || member.email}
                                        {member.email && member.full_name ? ` (${member.email})` : ''}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className={styles.noMembersMessage}>
                                <p>All workspace members are already part of this group, or there are no members available.</p>
                            </div>
                        )}
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className={styles.buttonRow}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading || availableMembers.length === 0 || loadingMembers}
                            >
                                {loading ? 'Adding...' : 'Add to Group'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}